import React, { useCallback, useEffect, useState } from 'react'
import { App, Alert, Button, DatePicker, Input, Modal, Space, Switch, Table, Tag, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { supabaseClient } from '../../lib/supabaseClient'
import { ListPage, SectionCard } from '../../theme/listkit'
import { edgeErrorMessage } from '../../lib/edgeError'
import { BulkPurgeCard } from './BulkPurge'

export interface FeedActivity {
  id: string
  text: string
  type: string
  created_at: string | null
  deleted_at: string | null
  author: string | null
  comment_count: number
  reaction_count: number
}
interface ListResult { ok: boolean; count: number; activities: FeedActivity[]; next?: string | null }

/**
 * Feed Moderation — remove or restore a single Stream activity.
 *
 * Reflected in the app in real time with no mobile work: the app watches
 * `flat:global`, so Stream's `feeds.activity.deleted` / `.restored` events are
 * applied by the SDK automatically.
 *
 * Soft delete is the default because it is REVERSIBLE (Stream sets `deleted_at`;
 * `restoreActivity` brings it back). Hard delete is a separate, explicitly
 * confirmed action — the friction is concentrated on the one step that cannot be
 * undone, rather than spread over the routine one.
 */
export function FeedModerationPage() {
  const { message } = App.useApp()
  const [rows, setRows] = useState<FeedActivity[]>([])
  const [showDeleted, setShowDeleted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [hardTarget, setHardTarget] = useState<FeedActivity | null>(null)
  const [hardConfirm, setHardConfirm] = useState('')
  /** Optional [from, to) window. Without it the list is "most recent N", which
   *  is why a soft bulk purge of an OLD window used to leave nothing to restore. */
  const [listWindow, setListWindow] = useState<[Dayjs, Dayjs] | null>(null)
  const [next, setNext] = useState<string | null>(null)

  const load = useCallback(
    async (includeDeleted: boolean, w: [Dayjs, Dayjs] | null, cursor?: string | null) => {
      setBusy(true)
      try {
        const { data, error } = await supabaseClient.functions.invoke<ListResult>('feed-moderate', {
          body: {
            action: 'list',
            include_deleted: includeDeleted,
            limit: 100,
            ...(w ? { from: w[0].toISOString(), to: w[1].toISOString() } : {}),
            ...(cursor ? { next: cursor } : {}),
          },
        })
        if (error) throw error
        // A cursor means "append the next page"; no cursor means a fresh query.
        setRows((prev) => (cursor ? [...prev, ...(data?.activities ?? [])] : data?.activities ?? []))
        setNext(data?.next ?? null)
      } catch (e) {
        message.error(await edgeErrorMessage(e, 'Failed to load the feed'))
      } finally {
        setBusy(false)
      }
    },
    [message],
  )

  useEffect(() => { void load(showDeleted, listWindow) }, [load, showDeleted, listWindow])

  const act = async (action: string, a: FeedActivity, confirmHard?: string) => {
    setBusy(true)
    try {
      const { error } = await supabaseClient.functions.invoke('feed-moderate', {
        body: { action, activity_id: a.id, ...(confirmHard ? { confirm_hard: confirmHard } : {}) },
      })
      if (error) throw error
      message.success(
        action === 'restore' ? 'Restored — it is back in the feed'
          : action === 'hard_delete' ? 'Permanently deleted'
          : 'Removed from the feed — restorable',
      )
      setHardTarget(null); setHardConfirm('')
      await load(showDeleted, listWindow)
    } catch (e) {
      message.error(await edgeErrorMessage(e, `Failed to ${action}`))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Feed Moderation',
        description:
          'Remove or restore posts in the community feed — one at a time, or in bulk across a date range. Removal is a soft delete by default and can be undone. A single removal reaches open apps immediately; restores and bulk purges may not appear until a member refreshes. Permanent deletion removes the post from GetStream outright and cannot be reversed.',
      }}
    >
      <SectionCard title="Community feed">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space>
            <Switch checked={showDeleted} onChange={setShowDeleted} />
            <Typography.Text>Show removed items (needed to restore something)</Typography.Text>
            <DatePicker.RangePicker
              showTime
              value={listWindow}
              onChange={(v) => setListWindow(v as [Dayjs, Dayjs] | null)}
              placeholder={['Any date', 'Any date']}
            />
            <Button onClick={() => void load(showDeleted, listWindow)} loading={busy}>Refresh</Button>
          </Space>

          <Table<FeedActivity>
            rowKey="id"
            size="small"
            loading={busy}
            dataSource={rows}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: 'Post',
                dataIndex: 'text',
                render: (t: string, r) => (
                  <Space direction="vertical" size={0}>
                    <Typography.Text delete={!!r.deleted_at}>{t || <em>(no text)</em>}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.type}</Typography.Text>
                  </Space>
                ),
              },
              { title: 'Author', dataIndex: 'author', width: 200 },
              {
                title: 'Engagement', width: 130,
                render: (_: unknown, r) => `${r.comment_count} 💬  ${r.reaction_count} ♥`,
              },
              {
                title: 'Posted', dataIndex: 'created_at', width: 170,
                render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
              },
              {
                title: 'Status', width: 110,
                render: (_: unknown, r) =>
                  r.deleted_at ? <Tag color="red">Removed</Tag> : <Tag color="green">Live</Tag>,
              },
              {
                title: 'Actions', width: 230,
                render: (_: unknown, r) =>
                  r.deleted_at ? (
                    <Space>
                      <Button size="small" onClick={() => void act('restore', r)}>Restore</Button>
                      <Button size="small" danger onClick={() => setHardTarget(r)}>Delete forever</Button>
                    </Space>
                  ) : (
                    <Button size="small" danger onClick={() => void act('soft_delete', r)}>Remove</Button>
                  ),
              },
            ]}
          />

          {next && (
            <Button onClick={() => void load(showDeleted, listWindow, next)} loading={busy} block>
              Load more
            </Button>
          )}

          {showDeleted && !listWindow && (
            <Alert
              type="info"
              showIcon
              message="Looking for something you bulk-purged?"
              description="This list is newest-first. If the purged window is older than what fits here, set the date range above to that window — otherwise the removed items will not appear and cannot be restored from this screen."
            />
          )}
        </Space>
      </SectionCard>

      <BulkPurgeCard onPurged={() => void load(showDeleted, listWindow)} />

      <Modal
        open={!!hardTarget}
        title="Permanently delete this post?"
        okText="Permanently delete"
        okButtonProps={{ danger: true, disabled: hardConfirm !== hardTarget?.id }}
        onOk={() => hardTarget && void act('hard_delete', hardTarget, hardConfirm)}
        onCancel={() => { setHardTarget(null); setHardConfirm('') }}
        confirmLoading={busy}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Paragraph>
            This cannot be undone. The post, its comments and its reactions are removed from
            Stream permanently. A record of the deletion — including the text — is kept in the
            audit log.
          </Typography.Paragraph>
          <Typography.Text>
            Type the activity id to confirm: <Typography.Text code copyable>{hardTarget?.id}</Typography.Text>
          </Typography.Text>
          <Input
            value={hardConfirm}
            onChange={(e) => setHardConfirm(e.target.value)}
            placeholder={hardTarget?.id}
            aria-label="Activity id confirmation"
          />
        </Space>
      </Modal>
    </ListPage>
  )
}
